import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Edit,
  Plus,
  Trash2,
  FilePlus,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  Upload,
  Download,
  Activity,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MerchantDetailsCard } from "@/components/merchant/MerchantDetailsCard";
import { MerchantContactInfo } from "@/components/merchant/MerchantContactInfo";
import { StoresTab } from "@/components/merchant/StoresTab";
import { OffersTab } from "@/components/merchant/OffersTab";
import { EditMerchantSheet } from "@/components/merchants/EditMerchantSheet";
import { MerchantDetailsSkeleton } from "@/components/merchant/MerchantDetailsSkeleton";
import { toast } from "sonner";
import { MerchantApi } from "@/api";
import { DocumentApi, DocumentUploadResponse } from "@/api/document.api";
import { IMerchant } from "@/interfaces/merchant.interfaces";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Mail, Phone } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { CountrySelect } from "@/components/ui/country-select";
import { LoaderButton } from "@/components/ui/loader-button";
import { formatDistanceToNow } from "date-fns";
import { ProgramApi } from "@/api/program.api";
import countries from "@/constants/array/country_city.json";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Mock data for offer categories
const offerCategories = [
  { id: "1", name: "Discount" },
  { id: "2", name: "Special Deal" },
  { id: "3", name: "Seasonal Offer" },
  { id: "4", name: "Flash Sale" },
  { id: "5", name: "Clearance" },
];

const documentUploadSchema = Yup.object().shape({
  documentName: Yup.string().required("Document name is required"),
  file: Yup.mixed().required("File is required"),
});

const contactSchema = Yup.object().shape({
  id: Yup.string().optional(),
  merchantId: Yup.string().required("MerchantId is required"),
  name: Yup.string()
    .required("Contact name is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  email: Yup.string()
    .required("Email is required")
    .email("Email must be a valid email address"),
  role: Yup.string()
    .required("Role is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  phoneCountry: Yup.string().required("Phone country is required"),
  phoneNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d+$/, "Phone must contain digits only")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number cannot exceed 15 digits"),
});

// Copy the MerchantSchema from CreateMerchant
const MerchantSchema = Yup.object().shape({
  businessName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant code is required."),
  businessLocation: Yup.string(),
  businessType: Yup.string().required("Business type is required."),
  businessDescription: Yup.string(),
  websiteUrl: Yup.string().url("Website URL must be a valid URL format"),
  country: Yup.string(),
  city: Yup.string(),
  taxId: Yup.string(),
  currencyVO: Yup.string().required("Payment currency is required"),
  refundPeriodInDays: Yup.string().required("Refund Period is required"),
  cashbackPercentage: Yup.number().required(
    "Commission Percentage is required"
  ),
  categoryVOList: Yup.array().of(
    Yup.object().shape({
      categoryCode: Yup.string(),
      categoryName: Yup.string().required("Category name is required"),
      cashbackPercentage: Yup.number()
        .typeError("Cashback percentage should be a number")
        .required("Cashback percentage is required")
        .positive("Cashback percentage must be greater than 0")
        .min(0.01, "Cashback percentage must be greater than 0")
        .max(100, "Cashback percentage cannot exceed 100%"),
    })
  ),
  contacts: Yup.array().of(
    Yup.object().shape({
      contactName: Yup.string().required("Contact name is required"),
      contactEmail: Yup.string()
        .required("Email is required")
        .email("Email must be a valid email address"),
      contactRole: Yup.string().required("Role is required"),
      merchantContactPhoneVOList: Yup.array().of(
        Yup.object().shape({
          programContactId: Yup.string(),
          phoneCountryCode: Yup.string(),
          phoneNumber: Yup.string(),
        })
      ),
    })
  ),
});

// Add CURRENCY_OPTIONS for payment currency dropdown
const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

const MerchantDetails = ({
  basePath = "/accesspoint",
}: {
  basePath?: string;
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Use merchant data from route state only
  const routeMerchant = location.state?.merchant;
  if (!routeMerchant) {
    return (
      <div className="p-8 text-center text-red-600 font-bold">
        No merchant data found. Please access this page from the merchants list.
      </div>
    );
  }
  const [merchant, setMerchant] = useState<IMerchant | null>(routeMerchant);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [isDeleteContactConfirmOpen, setIsDeleteContactConfirmOpen] =
    useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [addingContact, setAddingContact] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [uploadedDocument, setUploadedDocument] =
    useState<DocumentUploadResponse | null>(null);
  const [isDeleteDocumentOpen, setIsDeleteDocumentOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPage, setActivityPage] = useState(0);
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState("details");

  const form = useForm({
    resolver: yupResolver(MerchantSchema),
    defaultValues: {
      businessName: merchant.merchantName || "",
      merchantCode: merchant.merchantCode || "",
      cashbackPercentage: (merchant as any).cashbackPercentage ?? "",
      businessDescription: merchant.businessDescription || "",
      businessType: merchant.businessType || "",
      country: merchant.country || "",
      city: merchant.city || "",
      currencyVO: merchant.currencyVO?.id?.toString() || "",
      taxId: (merchant as any).taxId || "",
      refundPeriodInDays: merchant.refundPeriodInDays?.toString() || "",
      websiteUrl: merchant.websiteUrl || "",
      businessLocation: merchant.businessLocation || "",
      categoryVOList: merchant.categoryVOList || [],
      contacts: (merchant as any).contacts || [],
    },
  });

  async function fetchStores() {
    try {
      const response = await MerchantApi.getStoresUsingMerchantId(id);
      // Map API response to IStore shape
      const mappedStores = (response?.data || []).map((store: any) => ({
        id: store.id,
        merchantVO: store.merchantVO,
        storeName: store.storeName,
        address: store.address,
        isEpos: store.isEpos,
        city: store.city,
        latitude: store.latitude,
        longitude: store.longitude,
        zipCode: store.zipCode,
        // Offline store fields
        contactNumber: store.contactNumber,
        mapLink: store.mapLink,
        email: store.email,
        storeImageUri: store.storeImageUri,
      }));
      setStores(mappedStores);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch merchant stores");
    }
  }

  async function fetchOffers() {
    function mapApiOfferToUiOffer(apiOffer) {
      return {
        id: apiOffer.id,
        name: apiOffer.title,
        description: apiOffer.description,
        discountType: "Cashback",
        discountValue: apiOffer.cashbackPercentage
          ? `${apiOffer.cashbackPercentage}%`
          : "",
        status: apiOffer.isActive ? "Active" : "Inactive",
        category: apiOffer.categoryVO?.categoryName || "",
        startDate: apiOffer.startDateInMillis
          ? new Date(apiOffer.startDateInMillis).toISOString()
          : "",
        endDate: apiOffer.endDateInMillis
          ? new Date(apiOffer.endDateInMillis).toISOString()
          : "",
        bannerImage: apiOffer.imageUri,
        offerUrl: apiOffer.offerURL,
        terms: apiOffer.termsAndConditions,
        // Map offline merchant fields
        eligiblePurchase: apiOffer.eligiblePurchase || "",
        colorCode: apiOffer.colorCode || "#000000",
        // Include full API response for detailed view
        fullOfferData: apiOffer,
      };
    }
    try {
      const response = await MerchantApi.getMerchantOffers({
        startDateInMillis: "",
        endDateInMillis: "",
        merchantId: id,
        cashbackPercentage: "",
        sortOrder: "desc",
      });
      setOffers((response?.data || []).map(mapApiOfferToUiOffer));
    } catch (error) {
      toast.error(error?.message || "Failed to fetch merchant offers");
    }
  }

  async function fetchContacts() {
    try {
      const response = await MerchantApi.getMerchantContacts(id);
      setContacts(response?.data || []);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch merchant contacts");
    }
  }

  const fetchDocumentsForMerchant = async (merchantId: string) => {
    try {
      const { data } = await DocumentApi.getDocumentsForMerchant(
        parseInt(merchantId)
      );
      if (data && Array.isArray(data)) {
        const formattedDocuments = data.map((doc: any) => ({
          id: doc.id,
          name: doc.originalFileName,
          uploadedBy: doc.uploadedBy,
          uploadedDate: doc.uploadDateTime,
          size: `${(doc.fileSize / (1024 * 1024)).toFixed(1)} MB`,
          fileUrl: doc.fileUrl,
          documentName: doc.documentName,
          fileType: doc.fileType, // Add fileType field
        }));
        setDocuments(formattedDocuments);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setDocuments([]);
    }
  };

  const fetchActivityLogs = async (
    page: number = activityPage,
    pageSize: number = activityPageSize
  ) => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const merchantCode = merchant?.merchantCode;
      if (!merchantCode) {
        setActivityLogs([]);
        setActivityTotalRecords(0);
        setActivityLoading(false);
        return;
      }
      const payload = {
        id: null,
        programCode: null,
        merchantCode,
        pageNumber: page,
        pageSize: pageSize,
        sortOrder: "desc",
        sortBy: "createdAt",
      };
      const { data } = await ProgramApi.getActivityLogs(payload);
      setActivityLogs(data.activityLogVOs || []);
      setActivityTotalRecords(data.totalRecords || 0);
    } catch (err) {
      setActivityError("Failed to fetch activity logs");
    } finally {
      setActivityLoading(false);
    }
  };

  React.useEffect(() => {
    fetchStores();
    fetchOffers();
    fetchContacts();
    fetchDocumentsForMerchant(id);
  }, []);

  useEffect(() => {
    if (activeTab === "activity" && merchant?.merchantCode) {
      fetchActivityLogs();
    }
  }, [activeTab, merchant?.merchantCode, activityPage, activityPageSize]);

  const handleUpdateMerchant = async (updatedData: any) => {
    console.log("updatedData", updatedData);
    console.log("Logo data type:", typeof updatedData.logo);
    console.log("Logo data:", updatedData.logo);
    try {
      // Resolve country object from name
      const countryObj = (countries as any[]).find(
        (c) => c.name === updatedData.country
      );
      // Get username from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userName = parsedUserInfo?.username || "";

      // Build merchantVO payload exactly as in create flow
      const merchantVO = {
        id: routeMerchant?.id || merchant?.id,
        merchantCode:
          updatedData.merchantCode ||
          routeMerchant?.merchantCode ||
          merchant?.merchantCode,
        merchantName: updatedData.merchantName,

        businessName: updatedData.merchantName,
        businessType: updatedData.businessType,
        categoryVOList: updatedData.categoryVOList,
        businessDescription: updatedData.businessDescription,
        websiteUrl: updatedData.websiteUrl,
        offerDomain: updatedData.offerDomain, // Add offerDomain to the payload
        country: countryObj?.name,
        city: updatedData.city,
        businessLocation: updatedData.businessLocation,
        taxId: updatedData.taxId,
        currencyVO: { id: +updatedData.currency },
        refundPeriodInDays: updatedData.refundPeriodInDays,
        cashbackPercentage: updatedData.cashbackPercentage,
        createdDateInMillis:
          routeMerchant?.createdDateInMillis ||
          merchant?.createdDateInMillis ||
          new Date().getTime(),
        userName: userName,
      };

      console.log("merchantVO", merchantVO);
      const form = new FormData();
      if (updatedData.logo && typeof updatedData.logo !== "string") {
        // If logo is a File object, append it directly
        form.append("merchantLogo", updatedData.logo);
      } else if (
        updatedData.logo &&
        typeof updatedData.logo === "string" &&
        updatedData.logo.startsWith("data:")
      ) {
        // If logo is a base64 string, convert it to a File object
        const response = await fetch(updatedData.logo);
        const blob = await response.blob();
        const file = new File([blob], "merchant-logo.png", {
          type: "image/png",
        });
        form.append("merchantLogo", file);
      }
      const blob = new Blob([JSON.stringify(merchantVO)], {
        type: "application/json",
      });
      form.append("merchantVO", blob);
      const res = await MerchantApi.createUpdateMerchant(form);
      const apiData = res?.data;

      const currencyOption = CURRENCY_OPTIONS.find(
        (c) => c.id.toString() === String(updatedData.currency)
      );

      setMerchant((prev) => {
        if (!prev) return prev;

        const resolvedImageUri =
          apiData?.merchantImageUri ??
          apiData?.merchantImageURI ??
          (typeof updatedData.logo === "string"
            ? updatedData.logo
            : prev.merchantImageUri);

        const updatedCurrency = currencyOption
          ? {
              id: currencyOption.id,
              iso3CurrencyCode: currencyOption.value,
              currencyName: currencyOption.label,
              currencySymbol: prev.currencyVO?.currencySymbol ?? "",
            }
          : prev.currencyVO;

        return {
          ...prev,
          merchantName: updatedData.merchantName ?? prev.merchantName,
          merchantCode: updatedData.merchantCode ?? prev.merchantCode,
          businessDescription:
            updatedData.businessDescription ?? prev.businessDescription,
          businessType: updatedData.businessType ?? prev.businessType,
          websiteUrl: updatedData.websiteUrl ?? prev.websiteUrl,
          country: updatedData.country ?? countryObj?.name ?? prev.country,
          city: updatedData.city ?? prev.city,
          businessLocation:
            updatedData.businessLocation ?? prev.businessLocation,
          refundPeriodInDays:
            updatedData.refundPeriodInDays !== undefined &&
            updatedData.refundPeriodInDays !== ""
              ? Number(updatedData.refundPeriodInDays)
              : prev.refundPeriodInDays,
          categoryVOList: updatedData.categoryVOList ?? prev.categoryVOList,
          currencyVO: apiData?.currencyVO ?? updatedCurrency,
          merchantImageUri: resolvedImageUri,
          offerDomain: updatedData.offerDomain,
          taxId: updatedData.taxId,
        } as IMerchant;
      });

      toast.success("Merchant details updated successfully");
      setActivityPage(0);
      if (activeTab === "activity") {
        await fetchActivityLogs(0, activityPageSize);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update merchant");
    }
  };

  const handleAddContact = async (data: any) => {
    setAddingContact(true);
    try {
      // Create contact via API
      const contactData = {
        merchantId: parseInt(id),
        contactName: data.name,
        contactEmail: data.email,
        contactRole: data.role,
        merchantContactPhoneVOList: data.phoneNumber
          ? [
              {
                phoneCountryCode: data.phoneCountry || "",
                phoneNumber: data.phoneNumber,
              },
            ]
          : [],
      };

      const response = await MerchantApi.createOrUpdateMerchantContact(
        contactData
      );

      // Add the new contact to the local state
      const newContact = {
        id: response.data?.id || Date.now(),
        contactName: data.name,
        contactEmail: data.email,
        contactRole: data.role,
        merchantContactPhoneVOList: data.phoneNumber
          ? [
              {
                phoneCountryCode: data.phoneCountry || "",
                phoneNumber: data.phoneNumber,
              },
            ]
          : [],
      };

      setContacts([...contacts, newContact]);
      setIsAddContactDialogOpen(false);
      contactForm.reset();
      toast.success("Contact added successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to add contact");
    } finally {
      setAddingContact(false);
    }
  };

  function handleDeleteContact(contactId) {
    setContactToDelete(contactId);
    setIsDeleteContactConfirmOpen(true);
  }

  async function confirmDeleteContact() {
    try {
      await MerchantApi.deleteMerchantContact(contactToDelete);
      // Refresh contacts after deletion
      const contactsResponse = await MerchantApi.getMerchantContacts(id);
      setContacts(contactsResponse?.data || []);
      setIsDeleteContactConfirmOpen(false);
      setContactToDelete(null);
      toast.success("Contact deleted successfully");
    } catch (error: any) {
      console.error("Error deleting contact:", error);
      toast.error(error?.response?.data?.error_msg || "Failed to delete contact");
    }
  }

  // Form for document upload
  const documentUploadForm = useForm({
    resolver: yupResolver(documentUploadSchema),
    defaultValues: {
      documentName: "",
      file: null as File | null,
    },
  });

  // Form for adding new contact
  const contactForm = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      id: "",
      merchantId: id,
      phoneCountry: "",
      phoneNumber: "",
    },
  });

  const handleDocumentUpload = async (data: any) => {
    setIsDocumentUploading(true);
    try {
      const file = data.file;
      const documentName = data.documentName;

      const { data: response } = await DocumentApi.uploadDocumentForMerchant(
        file,
        {
          merchantId: parseInt(id),
          documentName: documentName,
        }
      );

      console.log("Upload response:", response);
      console.log("Response fileType:", response.fileType);

      // Extract fileType from filename if not provided by API
      const getFileTypeFromName = (filename: string) => {
        const extension = filename.split(".").pop()?.toLowerCase();
        return extension || "";
      };

      // Add the uploaded document to the documents list
      const newDocument = {
        id: response.id,
        name: response.originalFileName,
        uploadedBy: response.uploadedBy,
        uploadedDate: response.uploadDateTime,
        size: `${(response.fileSize / (1024 * 1024)).toFixed(1)} MB`,
        fileUrl: response.fileUrl,
        documentName: response.documentName,
        fileType:
          response.fileType || getFileTypeFromName(response.originalFileName), // Add fileType field with fallback
      };

      setDocuments([...documents, newDocument]);
      setUploadedDocument(response);
      setIsDocumentUploadOpen(false);
      documentUploadForm.reset();
      toast.success(
        `Document "${response.originalFileName}" uploaded successfully`
      );
    } catch (error) {
      toast.error(error?.message || "Failed to upload document");
    } finally {
      setIsDocumentUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: any) => {
    try {
      const response = await DocumentApi.downloadDocument(doc.id);
      const contentType =
        response.headers?.["content-type"] ||
        response.headers?.["Content-Type"] ||
        "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      const safeName = (doc.documentName || doc.name || "document").trim();
      a.download = safeName;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      toast.success(`Document "${doc.documentName}" downloaded successfully`);
    } catch (error) {
      toast.error(error?.message || "Failed to download document");
    }
  };

  const handleDeleteDocument = async (document: any) => {
    console.log("handleDeleteDocument called with:", document);
    console.log("document.documentName:", document?.documentName);
    setDocumentToDelete(document);
    setIsDeleteDocumentOpen(true);
    console.log("Modal should be opening now");
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      await DocumentApi.deleteDocument(documentToDelete.id);
      setDocuments(documents.filter((doc) => doc.id !== documentToDelete.id));
      toast.success(
        `Document "${documentToDelete.documentName}" deleted successfully`
      );
    } catch (error) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setIsDeleteDocumentOpen(false);
      setDocumentToDelete(null);
    }
  };

  const getFileIcon = (fileType: string) => {
    console.log("getFileIcon called with fileType:", fileType);
    const lower = fileType?.toLowerCase() || "";
    console.log("getFileIcon lower:", lower);

    // PDF Files - Red color with FileText icon
    if (lower.includes("pdf")) {
      console.log("Matched PDF - returning red icon");
      return (
        <span className="mr-3 text-red-500">
          <FileText className="h-5 w-5" />
        </span>
      );
    }

    // Word Documents - Blue color with FileText icon
    if (lower.includes("word") || lower.includes("doc")) {
      return (
        <span className="mr-3 text-blue-600">
          <FileText className="h-5 w-5" />
        </span>
      );
    }

    // Excel/Spreadsheet Files - Green color with FileSpreadsheet icon
    if (
      lower.includes("excel") ||
      lower.includes("spreadsheet") ||
      lower === "csv" ||
      lower === "xls" ||
      lower === "xlsx"
    ) {
      console.log("Matched Excel - returning green icon");
      return (
        <span className="mr-3 text-green-600">
          <FileSpreadsheet className="h-5 w-5" />
        </span>
      );
    }

    // Image Files - Purple color with FileImage icon
    if (
      lower.includes("image") ||
      lower.includes("jpg") ||
      lower.includes("jpeg") ||
      lower.includes("png") ||
      lower.includes("gif") ||
      lower.includes("bmp") ||
      lower.includes("svg") ||
      lower.includes("webp")
    ) {
      return (
        <span className="mr-3 text-purple-600">
          <FileImage className="h-5 w-5" />
        </span>
      );
    }

    // Code Files - Orange color with FileCode icon
    if (
      lower.includes("code") ||
      lower.includes("javascript") ||
      lower.includes("typescript") ||
      lower.includes("html") ||
      lower.includes("css") ||
      lower.includes("php") ||
      lower.includes("python") ||
      lower.includes("java") ||
      lower.includes("json") ||
      lower.includes("xml")
    ) {
      return (
        <span className="mr-3 text-orange-600">
          <FileCode className="h-5 w-5" />
        </span>
      );
    }

    // Default for other files - Gray color with FileText icon
    console.log("Using default icon - fileType:", fileType, "lower:", lower);
    return (
      <span className="mr-3 text-gray-500">
        <FileText className="h-5 w-5" />
      </span>
    );
  };

  if (isLoading) {
    return <MerchantDetailsSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to={`${basePath}/merchants`}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Merchant Details
            </h1>
            <p className="text-gray-500">
              View and manage merchant information
            </p>
          </div>
        </div>
        <PermissionGuard permissionCode={PERMISSION_CODES.UPDATE_MERCHANT}>
          <Button
            onClick={() => setEditSheetOpen(true)}
            className="flex items-center gap-2"
          >
            <Edit className="h-4 w-4" /> Edit Merchant
          </Button>
        </PermissionGuard>
      </div>

      {/* Merchant header card */}
      <MerchantDetailsCard merchant={merchant} />

      {/* Tabs for different sections */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Merchant Details
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger
            value="stores"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Stores
          </TabsTrigger>
          <TabsTrigger
            value="offers"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Offers
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          {/* Row 1: General Information & Business Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information Card */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Merchant Name
                  </p>
                  <p className="text-gray-800">{merchant?.merchantName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Merchant Code
                  </p>
                  <p className="text-gray-800">{merchant?.merchantCode}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Business Type
                  </p>
                  <Badge
                    variant="outline"
                    className="bg-blue-100 text-blue-800 hover:bg-blue-100"
                  >
                    {merchant?.businessType}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Description
                  </p>
                  <p className="text-gray-800">
                    {merchant?.businessDescription || "No description provided"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Website URL
                  </p>
                  <p className="text-gray-800">
                    {merchant?.websiteUrl ? (
                      <a
                        href={merchant.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {merchant.websiteUrl}
                      </a>
                    ) : (
                      "No website provided"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Offer Domain
                  </p>
                  <p className="text-gray-800">
                    {(merchant as any)?.offerDomain || "Not provided"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Business Details Card */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Business Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">
                      {merchant?.country || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">
                      {merchant?.city || "Not specified"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Business Location
                  </p>
                  <p className="text-gray-800">
                    {merchant?.businessLocation || "Not specified"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    VAT Number
                  </p>
                  <p className="text-gray-800">
                    {(merchant as any)?.taxId || "Not provided"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Financial Information & Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Financial Information Card */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Financial Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Payment Currency
                  </p>
                  <p className="text-gray-800">
                    {merchant?.currencyVO?.iso3CurrencyCode} (
                    {merchant?.currencyVO?.currencyName})
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Refund Period (days)
                  </p>
                  <p className="text-gray-800">
                    {merchant?.refundPeriodInDays !== undefined &&
                    merchant?.refundPeriodInDays !== null
                      ? merchant.refundPeriodInDays
                      : "Not specified"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Categories Card */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {merchant?.categoryVOList &&
                merchant.categoryVOList.length > 0 ? (
                  <div className="space-y-4">
                    {merchant.categoryVOList.map((category, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {category.categoryName}
                          </p>
                          {category.categoryCode && (
                            <p className="text-sm text-gray-500">
                              Code: {category.categoryCode}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-500">
                            Cashback Percentage
                          </p>
                          <p className="text-lg font-semibold text-blue-600">
                            {category.cashbackPercentage
                              ? `${category.cashbackPercentage}%`
                              : "0%"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No categories assigned to this merchant.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Merchant Contacts
              </CardTitle>
              <div className="flex gap-2">
                <PermissionGuard
                  permissionCode={PERMISSION_CODES.UPDATE_MERCHANT}
                >
                  <Button
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => setIsAddContactDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Add Contact
                  </Button>
                </PermissionGuard>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {contacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No contacts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Contacts Found</p>
                  <p className="mb-4">
                    No contacts are currently added to this merchant.
                  </p>
                  <PermissionGuard
                    permissionCode={PERMISSION_CODES.UPDATE_MERCHANT}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      className=" gap-2"
                      onClick={() => setIsAddContactDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4" /> Create Your First Contact
                    </Button>
                  </PermissionGuard>
                </div>
              ) : (
                <div className="space-y-6">
                  {contacts.map((contact, index) => (
                    <div
                      key={contact?.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 sm:mb-0">
                        <p className="font-medium text-gray-800">
                          {contact?.contactName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {contact?.contactRole}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center text-gray-500">
                          <Mail className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            {contact?.contactEmail}
                          </span>
                        </div>
                        {contact?.merchantContactPhoneVOList?.[0] && (
                          <div className="flex items-center text-gray-500">
                            <Phone className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              {
                                contact?.merchantContactPhoneVOList?.[0]
                                  .phoneNumber
                              }
                            </span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-red-600 p-1 h-auto"
                          onClick={() => handleDeleteContact(contact.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Contact Dialog */}
          <Dialog
            open={isAddContactDialogOpen}
            onOpenChange={(open) => {
              setIsAddContactDialogOpen(open);
              if (!open) {
                contactForm.reset();
              }
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
              </DialogHeader>
              <Form {...contactForm}>
                <form
                  onSubmit={contactForm.handleSubmit(handleAddContact)}
                  className="space-y-4"
                >
                  <FormField
                    control={contactForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold text-gray-700 text-base">
                          Name*
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter contact name"
                            {...field}
                            onChange={(e) => {
                              // Only allow letters and spaces
                              const value = e.target.value.replace(
                                /[^a-zA-Z\s]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold text-gray-700 text-base">
                          Email*
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Enter contact email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold text-gray-700 text-base">
                          Role*
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter contact role"
                            {...field}
                            onChange={(e) => {
                              // Only allow letters and spaces
                              const value = e.target.value.replace(
                                /[^a-zA-Z\s]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <FormField
                      control={contactForm.control}
                      name="phoneCountry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold text-gray-700 text-base">
                            Phone*
                          </FormLabel>
                          <FormControl>
                            <CountrySelect
                              value={field?.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="opacity-0">Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter phone number"
                              {...field}
                              onChange={(e) => {
                                // Only allow positive numbers
                                const value = e.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter className="sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsAddContactDialogOpen(false);
                        contactForm.reset();
                      }}
                    >
                      Cancel
                    </Button>
                    <LoaderButton type="submit" loading={addingContact}>
                      <Plus className="h-4 w-4 mr-2" /> Add Contact
                    </LoaderButton>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          {/* Delete Contact Confirmation Dialog */}
          <AlertDialog
            open={isDeleteContactConfirmOpen}
            onOpenChange={setIsDeleteContactConfirmOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this contact? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDeleteContact}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-gray-800">
                Merchant Documents
              </CardTitle>
              <div>
                <PermissionGuard
                  permissionCode={PERMISSION_CODES.UPDATE_MERCHANT}
                >
                  <Button
                    size="sm"
                    onClick={() => setIsDocumentUploadOpen(true)}
                  >
                    <FilePlus className="h-4 w-4 mr-2" /> Upload Document
                  </Button>
                </PermissionGuard>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No documents available. Upload your first document.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="py-4 flex flex-col sm:flex-row sm:items-center justify-between first:pt-0 last:pb-0"
                    >
                      <div className="flex items-center">
                        {getFileIcon(doc.fileType)}
                        <div>
                          <p className="font-medium text-gray-800">
                            {doc.documentName}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Uploaded by {doc.uploadedBy} •{" "}
                            {formatDistanceToNow(new Date(doc.uploadedDate), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mt-2 sm:mt-0">
                        <span className="text-sm text-gray-500 mr-4">
                          {doc.size}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 ml-2"
                          onClick={() => handleDeleteDocument(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stores Tab */}
        <TabsContent value="stores" className="space-y-6 pt-4">
          <StoresTab
            initialStores={stores}
            merchantId={id}
            merchantCode={merchant?.merchantCode}
            merchant={merchant}
          />
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-6 pt-4">
          <OffersTab
            initialOffers={offers}
            merchantId={id}
            merchantCode={merchant?.merchantCode}
            merchant={merchant}
          />
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {activityLoading ? (
                <div className="text-center text-gray-500">
                  Loading activity...
                </div>
              ) : activityError ? (
                <div className="text-center text-red-500">{activityError}</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center text-gray-500">
                  No activity found.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-md bg-blue-50 text-blue-600">
                          <Activity className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-2 items-center mb-1">
                            <span
                              className="font-medium text-gray-800 truncate max-w-xs"
                              title={log.actorName}
                            >
                              {log.actorName}
                            </span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {log.action
                                .replace(/_/g, " ")
                                .toLowerCase()
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                            <span className="text-xs text-gray-400">
                              {log.createdAt
                                ? formatDistanceToNow(new Date(log.createdAt), {
                                    addSuffix: true,
                                  })
                                : ""}
                            </span>
                          </div>
                          <div
                            className="text-gray-700 text-sm truncate"
                            title={log.description}
                          >
                            {log.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!activityLoading && activityTotalRecords > 0 && (
                <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Rows per page:</span>
                    <Select
                      value={activityPageSize.toString()}
                      onValueChange={(value) => {
                        setActivityPageSize(Number(value));
                        setActivityPage(0);
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue placeholder={activityPageSize.toString()} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {Math.ceil(activityTotalRecords / activityPageSize) > 1 && (
                    <EnhancedPaginationControl
                      totalPages={Math.ceil(
                        activityTotalRecords / activityPageSize
                      )}
                      currentPage={activityPage}
                      onPageChange={(page) => setActivityPage(page)}
                    />
                  )}
                  <div className="w-[160px]" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Merchant Sheet */}
      <EditMerchantSheet
        merchant={merchant}
        open={editSheetOpen}
        onOpenChange={setEditSheetOpen}
        onUpdate={handleUpdateMerchant}
      />

      {/* Document Upload Dialog */}
      <Dialog
        open={isDocumentUploadOpen}
        onOpenChange={setIsDocumentUploadOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for this merchant. Please provide a name for the
              document.
            </DialogDescription>
          </DialogHeader>
          <Form {...documentUploadForm}>
            <form
              onSubmit={documentUploadForm.handleSubmit(handleDocumentUpload)}
              className="space-y-4"
            >
              <FormField
                control={documentUploadForm.control}
                name="documentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Service Agreement, Terms & Conditions"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={documentUploadForm.control}
                name="file"
                render={({ field: { value, onChange, ...fieldProps } }) => {
                  function truncateFileName(name, max = 32) {
                    if (name.length <= max) return name;
                    const extIndex = name.lastIndexOf(".");
                    const ext = extIndex !== -1 ? name.slice(extIndex) : "";
                    const base = name.slice(
                      0,
                      extIndex !== -1 ? extIndex : undefined
                    );
                    const keep = Math.max(8, max - ext.length - 5);
                    return base.slice(0, keep) + "..." + base.slice(-5) + ext;
                  }
                  return (
                    <FormItem>
                      <FormLabel>File</FormLabel>
                      <FormControl>
                        <div className="space-y-3">
                          {!value ? (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                              onClick={() =>
                                document
                                  .getElementById(
                                    "document-file-input-merchant"
                                  )
                                  ?.click()
                              }
                            >
                              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600 mb-1">
                                Click to select a file or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, DOC, DOCX, XLS, XLSX up to 10MB
                              </p>
                            </div>
                          ) : value instanceof File ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md min-w-0">
                              <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span
                                  className="text-sm text-gray-700 block truncate"
                                  title={value.name}
                                >
                                  {truncateFileName(value.name)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onChange(null);
                                  const fileInput = document.getElementById(
                                    "document-file-input-merchant"
                                  ) as HTMLInputElement;
                                  if (fileInput) {
                                    fileInput.value = "";
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : null}
                          <input
                            id="document-file-input-merchant"
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error("File size exceeds 10MB limit.");
                                  return;
                                }
                                onChange(file);
                              }
                            }}
                            {...fieldProps}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <DialogFooter className="sm:justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDocumentUploadOpen(false);
                    documentUploadForm.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isDocumentUploading ||
                    !documentUploadForm.watch("file") ||
                    !documentUploadForm.watch("documentName")
                  }
                >
                  {isDocumentUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog
        open={isDeleteDocumentOpen}
        onOpenChange={setIsDeleteDocumentOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "
              {documentToDelete?.documentName || "Unknown Document"}"? This
              action cannot be undone.
              {/* Debug info */}
              <div style={{ display: "none" }}>
                Debug: documentToDelete = {JSON.stringify(documentToDelete)}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteDocumentOpen(false);
                setDocumentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteDocument}
            >
              Delete Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantDetails;
